import { useQuery } from '@tanstack/vue-query';
import { queryClient } from '@/queryClient';
import { useDepotStore } from '@store/depot';
import { useUserStore } from '@store/user';
import { useDiaryStore } from '@store/diary';

const STALE_TIME = 300_000; // 5 minutes

/** Matches the depots endpoint's own cap, so the option list is never a first page. */
const DEPOT_OPTION_LIMIT = 500;

/**
 * Query keys used by resource filter option queries.
 * Export so stores can invalidate specific caches on mutation.
 */
export const FILTER_QUERY_KEYS = {
    depots: ['resource-filter-depots'] as const,
    vesselTypes: ['resource-filter-vessel-types'] as const,
    containerSubtypes: ['resource-filter-container-subtypes'] as const,
    trailerSubtypes: ['resource-filter-trailer-subtypes'] as const,
    transportTypes: ['resource-filter-transport-types'] as const,
    actionTypes: ['resource-filter-action-types'] as const,
};

interface FilterOption {
    id: string | number;
    name: string;
}

interface SubtypeOption {
    id: string;
    name: string;
}

interface ActionTypeOption {
    value: string;
    label: string;
}

/**
 * Cached depot options — fetches via depot store, filters to active only.
 */
export function useDepotFilterOptions() {
    const depotStore = useDepotStore();
    const userStore = useUserStore();

    const {
        data: depotOptions,
        isLoading,
        isError,
    } = useQuery<FilterOption[]>({
        // Partitioned by tenant: without it, a tenant switch can serve the
        // previous tenant's depots from cache until staleTime expires.
        queryKey: computed(() => [...FILTER_QUERY_KEYS.depots, userStore.tenantId]),
        queryFn: async () => {
            // The depots endpoint defaults to 15 rows, so ask for the full list
            await depotStore.index({ limit: DEPOT_OPTION_LIMIT });
            return depotStore.collection
                .filter((depot: any) => depot.active === true || depot.active === 1 || depot.active === '1')
                .map((depot: any) => ({
                    id: depot.id,
                    name: depot.name,
                }))
                .sort((a: FilterOption, b: FilterOption) => String(a.name).localeCompare(String(b.name)));
        },
        staleTime: STALE_TIME,
        refetchOnWindowFocus: false,
    });

    return { depotOptions: computed(() => depotOptions.value ?? []), isLoading, isError };
}

/**
 * Cached vessel type (body type) options — from GET /transports/vessel-types.
 */
export function useVesselTypeFilterOptions() {
    const { data: bodyTypeOptions, isLoading } = useQuery<FilterOption[]>({
        queryKey: [...FILTER_QUERY_KEYS.vesselTypes],
        queryFn: async () => {
            const response = await (window as any).tenantAxios.get('/transports/vessel-types');
            const bodyTypeData = response.data.data || response.data || [];
            return bodyTypeData.map((bodyType: any) => ({
                id: bodyType.value,
                name: bodyType.label,
            }));
        },
        staleTime: STALE_TIME,
        refetchOnWindowFocus: false,
    });

    return { bodyTypeOptions: computed(() => bodyTypeOptions.value ?? []), isLoading };
}

/**
 * Cached transport type options — from GET /transports/types.
 */
export function useTransportTypeFilterOptions() {
    const { data: typeOptions, isLoading } = useQuery<FilterOption[]>({
        queryKey: [...FILTER_QUERY_KEYS.transportTypes],
        queryFn: async () => {
            const response = await (window as any).tenantAxios.get('/transports/types');
            const typeData = response.data.data || response.data || [];
            return typeData.map((type: any) => ({
                id: type.value || type.id,
                name: type.label || type.name,
            }));
        },
        staleTime: STALE_TIME,
        refetchOnWindowFocus: false,
    });

    return { typeOptions: computed(() => typeOptions.value ?? []), isLoading };
}

/**
 * Cached vessel subtype options — from GET /vessels/subtypes?type=<TYPE>.
 */
export function useVesselSubtypeFilterOptions(vesselType: 'CONTAINER' | 'TRAILER') {
    const queryKey =
        vesselType === 'CONTAINER' ? FILTER_QUERY_KEYS.containerSubtypes : FILTER_QUERY_KEYS.trailerSubtypes;

    const { data: subtypeOptions, isLoading } = useQuery<SubtypeOption[]>({
        queryKey: [...queryKey],
        queryFn: async () => {
            const response = await (window as any).tenantAxios.get(`/vessels/subtypes?type=${vesselType}`);
            const subtypeData = response.data.data || response.data || [];
            return subtypeData.map((subtype: any) => ({
                id: subtype.value,
                name: subtype.label,
            }));
        },
        staleTime: STALE_TIME,
        refetchOnWindowFocus: false,
    });

    return { subtypeOptions: computed(() => subtypeOptions.value ?? []), isLoading };
}

/**
 * Cached diary action type options — from GET /diaries/action-types.
 */
export function useActionTypeFilterOptions() {
    const diaryStore = useDiaryStore();

    const { data: actionTypeOptions, isLoading } = useQuery<ActionTypeOption[]>({
        queryKey: [...FILTER_QUERY_KEYS.actionTypes],
        queryFn: async () => {
            await diaryStore.getActionTypes();
            const actionTypeData = diaryStore.actionTypes || [];
            return Object.entries(actionTypeData).map(([key, value]) => ({
                value: key,
                label: value as string,
            }));
        },
        staleTime: STALE_TIME,
        refetchOnWindowFocus: false,
    });

    return { actionTypeOptions: computed(() => actionTypeOptions.value ?? []), isLoading };
}

/**
 * Invalidate all resource filter option caches.
 * Call from entity stores (depot, transport, vessel) after mutations.
 */
export function invalidateResourceFilterCache(keys?: (keyof typeof FILTER_QUERY_KEYS)[]) {
    const toInvalidate = keys ? keys.map((k) => FILTER_QUERY_KEYS[k]) : Object.values(FILTER_QUERY_KEYS);
    toInvalidate.forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey: [...queryKey] });
    });
}
